/**
 * Boundary normalization for incoming Goal records.
 *
 * The RYTE engine emits Goals in a "flat with nulls" wire shape — every
 * `executionWindow` field is present on the wire and absent values are
 * `null` (per the Smithy/Lambda response contract). The FE codebase
 * checks for absence with strict `=== undefined` comparisons in several
 * predicates (`openStartPredicates.isBugCondition` and others) — `null`
 * slips past those checks and produces wrong rendering.
 *
 * This helper maps `executionWindow.start: null` (and `delayedAfter: null`,
 * symmetrically) plus `parameters.failureMode: null` to omitted fields on a
 * fresh object, before the goal lands in the slice. Applied at every
 * slice-ingest site in `ryteDataSlice.ts`:
 *   - `upsertGoal` reducer (covers RTKQ mutation responses + dispatched
 *     WebSocket upserts — `parseRyteMessage` dispatches `upsertGoal`
 *     per goal, so the WS path is automatically covered)
 *   - `upsertManyGoals` reducer (batched WS upserts)
 *   - `replaceGoal` reducer (optimistic-temp swap path)
 *   - `getYardGoals.matchFulfilled` extraReducer (initial-load path)
 *
 * Pure — does not mutate input. Only allocates when at least one of
 * `executionWindow.start`, `executionWindow.delayedAfter` or `failureMode`
 * is `null`; otherwise returns the input reference unchanged so unmodified
 * goals don't burn allocations.
 *
 * REMOVE goals post-Part-1 omit `start` on the wire. The engine echoes
 * them back as `start: null`. Without this normalization, every REMOVE
 * would land in the slice with `start: null`, and downstream code
 * doing `start === undefined` checks would mis-treat them as having a
 * concrete start time.
 *
 * `failureMode` is the same hazard on a different field, and it shipped a
 * production defect (2026-08-20): `getYardGoals`' list projection emits
 * `failureMode: null` where a create response omits the key, so the two IB
 * Door Transfer G2 policy gates — which read `!== undefined` — classified
 * every page-loaded transfer graph as malformed and hid Edit/Remove. Both
 * gates now test positively as well; this boundary rewrite is the half that
 * also covers any FUTURE `=== undefined` reader of the field.
 *
 * @see .claude/specs/* — Part 1 of REMOVE wire-shape cleanup
 */

import type { Goal, GoalParameters } from "@/features/ryteTracker/types/goal";

/**
 * Returns a goal with `executionWindow.start`/`delayedAfter` and
 * `parameters.failureMode` omitted when the engine sent `null`. Other fields
 * and nested objects are left as-is.
 */
export const normalizeIncomingGoal = <T extends Goal>(goal: T): T => {
  const params = goal.parameters;
  if (!params) return goal;

  const ew = params.executionWindow;
  const startIsNull = ew?.start === null;
  const delayedIsNull = ew?.delayedAfter === null;
  const failureModeIsNull = params.failureMode === null;
  if (!startIsNull && !delayedIsNull && !failureModeIsNull) return goal;

  // Rest-destructured rather than deleted so `failureMode` is an ABSENT key,
  // matching how the executionWindow rewrite below omits rather than
  // `undefined`-assigns. `hasOwnProperty` is observable; `!== undefined` is not.
  const { failureMode: _nulledFailureMode, ...paramsSansFailureMode } = params;
  const nextParams: GoalParameters = failureModeIsNull ? paramsSansFailureMode : { ...params };

  // `ew` is re-tested rather than relying on `startIsNull || delayedIsNull` to
  // imply it: only the explicit test narrows the type, and an unreachable `ew?.`
  // inside the block would be a branch no input can exercise.
  if (ew && (startIsNull || delayedIsNull)) {
    const nextEw: { start?: number; delayedAfter?: number } = {};
    if (!startIsNull && ew.start !== undefined) nextEw.start = ew.start;
    if (!delayedIsNull && ew.delayedAfter !== undefined) nextEw.delayedAfter = ew.delayedAfter;
    nextParams.executionWindow = nextEw;
  }

  return { ...goal, parameters: nextParams };
};
